import { useState } from 'react';
import './App.css';

const API_BASE = 'https://rr9smrmu78.execute-api.us-east-1.amazonaws.com/production';

const formatPhone = (phone) => {
  if (phone === null || phone === undefined) return '—';
  const value = String(phone).trim();
  if (!value) return '—';
  if (value.toLowerCase().startsWith('unknown')) return '—';
  return value;
};

const formatStatus = (status) => {
  switch (status) {
    case 'Confirmo':
      return 'Confirmado';
    case 'No ire':
      return 'No irá';
    case 'Sin respuesta':
      return 'Sin respuesta';
    default:
      return status || 'Sin respuesta';
  }
};

const statusClasses = (status) => {
  switch (status) {
    case 'Confirmo':
      return 'bg-sage/10 text-sage';
    case 'No ire':
      return 'bg-terracotta/10 text-terracotta';
    default:
      return 'bg-sage/5 text-sage/60';
  }
};

const LoginScreen = ({ passkey, setPasskey, onSubmit, loading, error }) => (
  <div className="min-h-screen bg-cream flex items-center justify-center px-6">
    <form
      onSubmit={onSubmit}
      className="w-full max-w-sm bg-white border border-border rounded-lg shadow-lg p-8"
    >
      <h1 className="font-display text-3xl text-sage text-center mb-2">Admin</h1>
      <p className="font-body text-sm text-sage/60 text-center mb-6">
        Ingresa el passkey para continuar
      </p>
      <input
        type="password"
        value={passkey}
        onChange={(e) => setPasskey(e.target.value)}
        placeholder="Passkey"
        autoFocus
        className="w-full border border-sage/30 rounded px-4 py-3 font-body text-sage mb-4 text-center tracking-[0.2em] focus:outline-none focus:border-sage"
      />
      {error && (
        <p className="font-body text-sm text-terracotta text-center mb-4">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || !passkey.trim()}
        className="w-full bg-terracotta text-cream font-body text-xs uppercase tracking-[0.15em] px-10 py-3 hover:bg-terracotta/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Verificando...' : 'Entrar'}
      </button>
    </form>
  </div>
);

const isConfirmed = (status) => status === 'Confirmo';
const isNotAttending = (status) => status === 'No ire';
const isAwaiting = (status) => !isConfirmed(status) && !isNotAttending(status);

const SummaryCards = ({ stats }) => {
  const cards = [
    { label: 'Pases', value: stats.parties },
    { label: 'Invitados', value: stats.total },
    { label: 'Confirmados', value: stats.confirmed },
    { label: 'No asistirán', value: stats.notAttending },
    { label: 'Sin respuesta', value: stats.awaiting },
  ];

  return (
    <section
      aria-label="Resumen de respuestas"
      className="grid grid-cols-2 sm:grid-cols-5 gap-3"
    >
      {cards.map((card) => (
        <div
          key={card.label}
          className="bg-white border border-border rounded-lg shadow-sm px-4 py-4 text-center"
        >
          <p className="font-display text-3xl text-sage leading-none">{card.value}</p>
          <p className="font-body text-[10px] uppercase tracking-[0.2em] text-sage/60 mt-2">
            {card.label}
          </p>
        </div>
      ))}
    </section>
  );
};

const AddGuestForm = ({ passkey, onAdded }) => {
  const [name, setName] = useState('');
  const [party, setParty] = useState('');
  const [phone, setPhone] = useState('');
  const [editableName, setEditableName] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const reset = () => {
    setName('');
    setParty('');
    setPhone('');
    setEditableName(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError('');
    setSuccess('');

    const trimmedName = name.trim();
    const trimmedParty = party.trim();
    if (!trimmedName) {
      setError('El nombre es requerido');
      return;
    }
    if (!trimmedParty) {
      setError('El número de pase es requerido');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/guests/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passkey: passkey.trim(),
          name: trimmedName,
          party: Number(trimmedParty),
          phone: phone.trim(),
          editableName,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        setError('Passkey incorrecto');
        return;
      }
      if (response.status === 409) {
        setError('Ya existe un invitado con ese pase');
        return;
      }
      if (!response.ok) {
        setError(data.error || 'Error al agregar invitado');
        return;
      }

      setSuccess(`Agregado: ${trimmedName}`);
      reset();
      if (onAdded) onAdded();
    } catch (err) {
      setError('Error de red. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white border border-border rounded-lg shadow-sm p-5">
      <h2 className="font-display text-xl text-sage mb-4">Agregar invitado</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-sage/70">
              Nombre
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre completo"
              className="mt-1 w-full border border-sage/30 rounded px-3 py-2 font-body text-sage focus:outline-none focus:border-sage"
            />
          </label>
          <label className="block">
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-sage/70">
              Número de pase
            </span>
            <input
              type="number"
              value={party}
              onChange={(e) => setParty(e.target.value)}
              placeholder="Ej. 1"
              min="1"
              className="mt-1 w-full border border-sage/30 rounded px-3 py-2 font-body text-sage focus:outline-none focus:border-sage"
            />
          </label>
          <label className="block">
            <span className="font-body text-[10px] uppercase tracking-[0.2em] text-sage/70">
              Teléfono <span className="normal-case text-sage/40">(opcional)</span>
            </span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10 dígitos"
              className="mt-1 w-full border border-sage/30 rounded px-3 py-2 font-body text-sage focus:outline-none focus:border-sage"
            />
          </label>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={editableName}
            onChange={(e) => setEditableName(e.target.checked)}
            className="h-4 w-4 accent-terracotta"
          />
          <span className="font-body text-sm text-sage">
            Permitir que el invitado edite su nombre
          </span>
        </label>

        {error && (
          <p className="font-body text-sm text-terracotta">{error}</p>
        )}
        {success && (
          <p className="font-body text-sm text-sage">{success}</p>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="bg-terracotta text-cream font-body text-xs uppercase tracking-[0.15em] px-8 py-3 hover:bg-terracotta/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Agregando...' : 'Agregar'}
          </button>
        </div>
      </form>
    </section>
  );
};

const PartyCard = ({ party, guests }) => {
  const confirmed = guests.filter((g) => isConfirmed(g.status)).length;

  return (
    <article className="bg-white border border-border rounded-lg shadow-sm overflow-hidden">
      <header className="bg-sage px-5 py-3 flex items-center justify-between">
        <h2 className="font-display text-xl text-cream">Pase #{party}</h2>
        <span className="font-body text-[10px] uppercase tracking-[0.2em] text-cream/80">
          {confirmed}/{guests.length}
        </span>
      </header>
    <ul className="divide-y divide-border">
      {guests.map((guest) => (
        <li
          key={`${guest.id}-${guest.party}`}
          className="px-5 py-4 grid grid-cols-12 gap-x-3 gap-y-1 items-center"
        >
          <div className="col-span-12 sm:col-span-5">
            <p className="font-display text-lg text-sage leading-tight">
              {guest.name || 'Sin nombre'}
            </p>
          </div>
          <div className="col-span-7 sm:col-span-4">
            <p className="font-mono text-sm text-sage/80 tracking-wider">
              {formatPhone(guest.phone)}
            </p>
          </div>
          <div className="col-span-5 sm:col-span-3 sm:text-right">
            <span
              className={`inline-block font-body text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${statusClasses(guest.status)}`}
            >
              {formatStatus(guest.status)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  </article>
  );
};

function Admin() {
  const [passkey, setPasskey] = useState('');
  const [authError, setAuthError] = useState('');
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guests, setGuests] = useState([]);
  const [fetchError, setFetchError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passkey.trim()) return;

    setLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_BASE}/guests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passkey: passkey.trim() }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        setAuthError('Passkey incorrecto');
        return;
      }
      if (!response.ok) {
        setAuthError(data.error || 'Error al autenticar');
        return;
      }

      setGuests(Array.isArray(data.guests) ? data.guests : []);
      setAuthed(true);
    } catch (err) {
      setAuthError('Error de red. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthed(false);
    setPasskey('');
    setAuthError('');
    setGuests([]);
    setFetchError('');
  };

  const handleRefresh = async () => {
    setLoading(true);
    setFetchError('');
    try {
      const response = await fetch(`${API_BASE}/guests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passkey: passkey.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setFetchError(data.error || 'Error al cargar los invitados');
        return;
      }
      setGuests(Array.isArray(data.guests) ? data.guests : []);
    } catch (err) {
      setFetchError('Error de red. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <LoginScreen
        passkey={passkey}
        setPasskey={setPasskey}
        onSubmit={handleLogin}
        loading={loading}
        error={authError}
      />
    );
  }

  const parties = guests.reduce((acc, guest) => {
    const key = guest.party ?? 'Sin pase';
    if (!acc[key]) acc[key] = [];
    acc[key].push(guest);
    return acc;
  }, {});

  const partyKeys = Object.keys(parties).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isNaN(na) || Number.isNaN(nb)) return String(a).localeCompare(String(b));
    return na - nb;
  });

  const stats = {
    parties: partyKeys.length,
    total: guests.length,
    confirmed: guests.filter((g) => isConfirmed(g.status)).length,
    notAttending: guests.filter((g) => isNotAttending(g.status)).length,
    awaiting: guests.filter((g) => isAwaiting(g.status)).length,
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-10 bg-cream/95 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <h1 className="font-display text-2xl text-sage">Admin · Invitados</h1>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="font-body text-[10px] uppercase tracking-[0.2em] text-sage/70 hover:text-sage transition-colors disabled:opacity-50"
            >
              {loading ? 'Cargando...' : 'Refrescar'}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="font-body text-[10px] uppercase tracking-[0.2em] text-sage/70 hover:text-terracotta transition-colors"
            >
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {fetchError && (
          <p className="font-body text-sm text-terracotta text-center">{fetchError}</p>
        )}

        {partyKeys.length > 0 && <SummaryCards stats={stats} />}

        <AddGuestForm passkey={passkey} onAdded={handleRefresh} />

        {partyKeys.length === 0 && !loading && (
          <p className="font-body text-sm text-sage/60 text-center py-12">
            No hay invitados para mostrar.
          </p>
        )}

        {partyKeys.map((partyKey) => (
          <PartyCard
            key={partyKey}
            party={partyKey}
            guests={parties[partyKey]}
          />
        ))}
      </main>
    </div>
  );
}

export default Admin;
